import { useCallback, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import * as ExcelJS from "exceljs";
import {
  getPubMissionLogList,
  SellerPubMissionLogCategoryFilter,
  SellerPubMissionLogItemResponse,
  SellerPubMissionLogListRequest,
  SellerPubMissionLogTypeFilter,
} from "../../../api/pub-mission-log";
import { pubState } from "../../../recoil/pub.atom";

const LIST_LIMIT = 10;
const EXCEL_LIMIT = 50;

export const useEventLogs = () => {
  const pub = useRecoilValue(pubState);

  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [list, setList] = useState<SellerPubMissionLogItemResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [nicknameInput, setNicknameInput] = useState("");
  const [nickname, setNickname] = useState("");
  const [processorInput, setProcessorInput] = useState("");
  const [processorNickname, setProcessorNickname] = useState("");
  const [category, setCategory] = useState<SellerPubMissionLogCategoryFilter>(
    SellerPubMissionLogCategoryFilter.ALL,
  );
  const [logType, setLogType] = useState<SellerPubMissionLogTypeFilter>(
    SellerPubMissionLogTypeFilter.ALL,
  );

  const buildFilterParams = useCallback(
    (
      page: number,
      limit: number,
    ): SellerPubMissionLogListRequest | null => {
      if (!pub) return null;

      return {
        pubId: pub.id,
        page,
        limit,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        nickname: nickname.trim() || undefined,
        category:
          category === SellerPubMissionLogCategoryFilter.ALL
            ? undefined
            : category,
        logType:
          logType === SellerPubMissionLogTypeFilter.ALL ? undefined : logType,
        processorNickname: processorNickname.trim() || undefined,
      };
    },
    [pub, startDate, endDate, nickname, category, logType, processorNickname],
  );

  const fetchList = useCallback(async () => {
    const params = buildFilterParams(currentPage, LIST_LIMIT);
    if (!params) return;

    setLoading(true);
    try {
      const res = await getPubMissionLogList(params);
      setList(res.list);
      setTotal(res.total);
    } catch (error: any) {
      alert(error?.message || "처리 내역을 불러오지 못했습니다.");
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, currentPage]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const applyTextFilters = () => {
    setNickname(nicknameInput.trim());
    setProcessorNickname(processorInput.trim());
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: SellerPubMissionLogCategoryFilter) => {
    setCategory(value);
    setCurrentPage(1);
  };

  const handleLogTypeChange = (value: SellerPubMissionLogTypeFilter) => {
    setLogType(value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const downloadExcel = async () => {
    if (!pub) return;

    const appliedNickname = nicknameInput.trim();
    const appliedProcessor = processorInput.trim();
    setNickname(appliedNickname);
    setProcessorNickname(appliedProcessor);

    setExcelDownloading(true);
    try {
      const baseFilter: Omit<SellerPubMissionLogListRequest, "page" | "limit"> =
        {
          pubId: pub.id,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          nickname: appliedNickname || undefined,
          category:
            category === SellerPubMissionLogCategoryFilter.ALL
              ? undefined
              : category,
          logType:
            logType === SellerPubMissionLogTypeFilter.ALL ? undefined : logType,
          processorNickname: appliedProcessor || undefined,
        };

      const first = await getPubMissionLogList({
        ...baseFilter,
        page: 1,
        limit: EXCEL_LIMIT,
      });
      const allRows: SellerPubMissionLogItemResponse[] = [...first.list];
      const totalCount = first.total;
      const totalPages = Math.ceil(totalCount / EXCEL_LIMIT);

      for (let page = 2; page <= totalPages; page += 1) {
        const res = await getPubMissionLogList({
          ...baseFilter,
          page,
          limit: EXCEL_LIMIT,
        });
        allRows.push(...res.list);
      }

      if (allRows.length === 0) {
        alert("다운로드할 처리 내역이 없습니다.");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("처리내역");

      worksheet.columns = [
        { header: "처리 일시", key: "createdAt", width: 18 },
        { header: "닉네임", key: "nickname", width: 16 },
        { header: "미션·빙고명", key: "missionName", width: 24 },
        { header: "처리 유형", key: "logTypeLabel", width: 18 },
        { header: "값", key: "value", width: 16 },
        { header: "취소 사유", key: "memo", width: 30 },
        { header: "처리자", key: "processorNickname", width: 16 },
      ];

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF3EBFF" },
      };

      allRows.forEach((row) => {
        worksheet.addRow({
          createdAt: row.createdAt,
          nickname: row.nickname,
          missionName: row.missionName,
          logTypeLabel: row.logTypeLabel,
          value: row.value ?? "",
          memo: row.memo ?? "",
          processorNickname: row.processorNickname ?? "",
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `처리내역_${pub?.pubName || "매장"}_${dateStr}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error(error);
      alert(error?.message || "엑셀 다운로드에 실패했습니다.");
    } finally {
      setExcelDownloading(false);
    }
  };

  return {
    list,
    loading,
    total,
    totalPages: Math.max(1, Math.ceil(total / LIST_LIMIT)),
    currentPage,
    setCurrentPage,
    startDate,
    endDate,
    nicknameInput,
    setNicknameInput,
    processorInput,
    setProcessorInput,
    category,
    logType,
    handleCategoryChange,
    handleLogTypeChange,
    handleStartDateChange,
    handleEndDateChange,
    applyTextFilters,
    downloadExcel,
    excelDownloading,
  };
};
